import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { Button } from '../components/ui/Button';
import { FileText, Search, Plus, Trash2, Users, Loader2, Filter, Tag, X, Calendar, Import as SortAsc, Dessert as SortDesc, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTranslation } from '../lib/i18n';

interface Document {
  id: string;
  title: string;
  updated_at: string;
  created_at: string;
  shared_role?: string;
  tags?: string[];
  thumbnail_url?: string | null;
}

type SortField = 'title' | 'created_at' | 'updated_at';
type SortOrder = 'asc' | 'desc';

const Documents = () => {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [sharedDocuments, setSharedDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingShared, setLoadingShared] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'owned' | 'shared'>('owned');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('updated_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');

  useEffect(() => {
    if (user) {
      loadDocuments();
      loadSharedDocuments();
    }
  }, [user]);

  const loadDocuments = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('documents')
        .select('id, title, created_at, updated_at, thumbnail_url')
        .eq('user_id', user?.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      if (data && data.length > 0) {
        const documentIds = data.map(doc => doc.id);
        const { data: tagsData, error: tagsError } = await supabase
          .from('document_tags')
          .select('document_id, tag')
          .in('document_id', documentIds);

        if (!tagsError && tagsData) {
          const tagsByDoc = tagsData.reduce((acc, { document_id, tag }) => {
            if (!acc[document_id]) acc[document_id] = [];
            acc[document_id].push(tag);
            return acc;
          }, {} as Record<string, string[]>);

          const docsWithTags = data.map(doc => ({
            ...doc,
            tags: tagsByDoc[doc.id] || []
          }));

          const allTags = Array.from(new Set(tagsData.map(t => t.tag))).sort();
          setAvailableTags(allTags);
          setDocuments(docsWithTags);
          return;
        }
      }

      setDocuments(data || []);
    } catch (error) {
      console.error('Error loading documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSharedDocuments = async () => {
    try {
      setLoadingShared(true);
      const { data, error } = await supabase
        .from('document_shares')
        .select(`
          document_id,
          role_type,
          documents (
            id,
            title,
            created_at,
            updated_at
          )
        `)
        .eq('shared_with', user?.id);

      if (error) throw error;

      const formattedSharedDocs = data
        ?.filter(share => share.documents)
        .map(share => ({
          ...share.documents,
          shared_role: share.role_type
        }));

      setSharedDocuments(formattedSharedDocs || []);
    } catch (error) {
      console.error('Error loading shared documents:', error);
    } finally {
      setLoadingShared(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedDocs.length) return;

    const confirmed = window.confirm(
      t('documents.confirmDelete', {
        count: selectedDocs.length,
        plural: selectedDocs.length === 1 ? '' : 's'
      })
    );

    if (!confirmed) return;

    try {
      setDeleteError(null);

      const { error: sharesError } = await supabase
        .from('document_shares')
        .delete()
        .in('document_id', selectedDocs);

      if (sharesError) throw sharesError;

      const { error: docsError } = await supabase
        .from('documents')
        .delete()
        .in('id', selectedDocs)
        .eq('user_id', user?.id);

      if (docsError) throw docsError;

      setDocuments((prev) => prev.filter((doc) => !selectedDocs.includes(doc.id)));
      setSelectedDocs([]);
    } catch (error: any) {
      console.error('Error deleting documents:', error);
      setDeleteError(error.message || t('documents.failedDelete'));
    }
  };

  const getFilteredDocuments = () => {
    let docs = activeTab === 'owned' ? documents : sharedDocuments;

    if (searchQuery) {
      docs = docs.filter((doc) =>
        doc.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    if (selectedTags.length > 0) {
      docs = docs.filter((doc) =>
        doc.tags && selectedTags.some(tag => doc.tags!.includes(tag))
      );
    }

    if (dateFilter !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      docs = docs.filter((doc) => {
        const docDate = new Date(doc.updated_at);
        switch (dateFilter) {
          case 'today':
            return docDate >= today;
          case 'week': {
            const weekAgo = new Date(today);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return docDate >= weekAgo;
          }
          case 'month': {
            const monthAgo = new Date(today);
            monthAgo.setMonth(monthAgo.getMonth() - 1);
            return docDate >= monthAgo;
          }
          default:
            return true;
        }
      });
    }

    docs = [...docs].sort((a, b) => {
      let aVal: any, bVal: any;
      switch (sortField) {
        case 'title':
          aVal = a.title.toLowerCase();
          bVal = b.title.toLowerCase();
          break;
        case 'created_at':
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
          break;
        case 'updated_at':
          aVal = new Date(a.updated_at).getTime();
          bVal = new Date(b.updated_at).getTime();
          break;
      }
      return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
    });

    return docs;
  };

  const filteredDocuments = getFilteredDocuments();

  const toggleTag = (tag: string) => {
    setSelectedTags(prev =>
      prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
    );
  };

  const clearFilters = () => {
    setSelectedTags([]);
    setDateFilter('all');
    setSearchQuery('');
  };

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  const toggleDocumentSelection = (docId: string) => {
    setSelectedDocs((prev) =>
      prev.includes(docId) ? prev.filter((id) => id !== docId) : [...prev, docId]
    );
  };

  if (!user) {
    return (
      <div className="text-center py-16">
        <h2 className="text-2xl font-bold mb-4">{t('documents.pleaseSignIn')}</h2>
        <p className="mb-8">{t('documents.pleaseSignInMessage')}</p>
        <Link to="/auth">
          <Button>{t('common.signIn')}</Button>
        </Link>
      </div>
    );
  }

  const isLoading = activeTab === 'owned' ? loading : loadingShared;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold">{t('documents.myDocuments')}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {activeTab === 'owned' && selectedDocs.length > 0 && (
            <Button variant="destructive" size="sm" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('documents.deleteSelected')}</span>
            </Button>
          )}
          <Link to="/editor">
            <Button size="sm">
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">{t('documents.newDocument')}</span>
              <span className="sm:hidden">{t('documents.newDocument')}</span>
            </Button>
          </Link>
        </div>
      </div>

      {deleteError && (
        <div className="bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-200 p-4 rounded-lg text-sm">
          {deleteError}
        </div>
      )}

      {/* Tabs */}
      <div className="flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 w-full sm:w-auto">
        <button
          onClick={() => setActiveTab('owned')}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors ${
            activeTab === 'owned'
              ? 'bg-blue-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <FileText className="w-4 h-4" />
          <span>{t('documents.myDocuments')}</span>
        </button>
        <button
          onClick={() => setActiveTab('shared')}
          className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium transition-colors border-l border-gray-200 dark:border-gray-700 ${
            activeTab === 'shared'
              ? 'bg-blue-600 text-white'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Users className="w-4 h-4" />
          <span>{t('documents.sharedWithMe')}</span>
        </button>
      </div>

      {/* Search + Filter row */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('documents.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
          />
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium transition-colors ${
            showFilters
              ? 'bg-blue-600 text-white border-blue-600'
              : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Filter className="w-4 h-4" />
          <span className="hidden sm:inline">{t('documents.filters')}</span>
        </button>
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">{t('documents.filters')}</h3>
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X className="w-3 h-3" />
              {t('documents.clearAll')}
            </button>
          </div>

          <div>
            <label className="flex items-center gap-1 text-sm font-medium mb-2">
              <Calendar className="w-3.5 h-3.5" />
              {t('documents.dateRange')}
            </label>
            <div className="flex gap-2 flex-wrap">
              {(['all', 'today', 'week', 'month'] as const).map((range) => (
                <button
                  key={range}
                  onClick={() => setDateFilter(range)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    dateFilter === range
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {range === 'all' ? t('documents.allTime') : range === 'today' ? t('documents.today') : range === 'week' ? t('documents.pastWeek') : t('documents.pastMonth')}
                </button>
              ))}
            </div>
          </div>

          {availableTags.length > 0 && (
            <div>
              <label className="flex items-center gap-1 text-sm font-medium mb-2">
                <Tag className="w-3.5 h-3.5" />
                {t('documents.tags')}
              </label>
              <div className="flex gap-2 flex-wrap">
                {availableTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => toggleTag(tag)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                      selectedTags.includes(tag)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-2">{t('documents.sortBy')}</label>
            <div className="flex gap-2 flex-wrap">
              {(['title', 'created_at', 'updated_at'] as const).map((field) => (
                <button
                  key={field}
                  onClick={() => toggleSort(field)}
                  className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    sortField === field
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {field === 'title' ? t('documents.sortTitle') : field === 'created_at' ? t('documents.sortCreated') : t('documents.sortUpdated')}
                  {sortField === field && (
                    sortOrder === 'asc' ? <SortAsc className="w-3 h-3" /> : <SortDesc className="w-3 h-3" />
                  )}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
            {t('documents.showing', { count: filteredDocuments.length, total: (activeTab === 'owned' ? documents : sharedDocuments).length })}
          </p>
        </div>
      )}

      {/* Document List */}
      {isLoading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600 dark:text-gray-300 text-sm">
            {activeTab === 'owned' ? t('documents.loadingOwned') : t('documents.loadingShared')}
          </p>
        </div>
      ) : filteredDocuments.length > 0 ? (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden space-y-2">
            {filteredDocuments.map((doc) => (
              <div key={doc.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                <Link to={`/editor/${doc.id}`} className="flex items-center gap-3 p-3 active:bg-gray-50 dark:active:bg-gray-700">
                  {/* Thumbnail */}
                  {doc.thumbnail_url ? (
                    <div className="w-12 h-12 flex-shrink-0 rounded-lg overflow-hidden">
                      <img src={doc.thumbnail_url} alt={doc.title} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="w-12 h-12 flex-shrink-0 bg-blue-50 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-400" />
                    </div>
                  )}

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 dark:text-white text-sm truncate">{doc.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {new Date(doc.updated_at).toLocaleDateString()}
                    </p>
                    {doc.shared_role && (
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        doc.shared_role === 'editor'
                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
                          : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {doc.shared_role}
                      </span>
                    )}
                    {doc.tags && doc.tags.length > 0 && (
                      <div className="flex gap-1 mt-1 flex-wrap">
                        {doc.tags.map(tag => (
                          <span key={tag} className="px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-xs rounded-full">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Checkbox (owned only) + arrow */}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {activeTab === 'owned' && (
                      <div
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleDocumentSelection(doc.id); }}
                        className="p-1"
                      >
                        <input
                          type="checkbox"
                          checked={selectedDocs.includes(doc.id)}
                          readOnly
                          className="rounded w-4 h-4"
                        />
                      </div>
                    )}
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </Link>
              </div>
            ))}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  {activeTab === 'owned' && (
                    <th className="w-8 px-6 py-3">
                      <input
                        type="checkbox"
                        checked={selectedDocs.length === filteredDocuments.length && filteredDocuments.length > 0}
                        onChange={() =>
                          setSelectedDocs(
                            selectedDocs.length === filteredDocuments.length
                              ? []
                              : filteredDocuments.map((doc) => doc.id)
                          )
                        }
                        className="rounded"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-white uppercase tracking-wider">
                    {t('documents.tableTitle')}
                  </th>
                  {activeTab === 'shared' && (
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-white uppercase tracking-wider">
                      {t('documents.tableRole')}
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-white uppercase tracking-wider">
                    {t('documents.tableCreated')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-white uppercase tracking-wider">
                    {t('documents.tableLastUpdated')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-gray-700 dark:text-white uppercase tracking-wider">
                    {t('documents.tableActions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredDocuments.map((doc) => (
                  <tr key={doc.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    {activeTab === 'owned' && (
                      <td className="px-6 py-4">
                        <input
                          type="checkbox"
                          checked={selectedDocs.includes(doc.id)}
                          onChange={() => toggleDocumentSelection(doc.id)}
                          className="rounded"
                        />
                      </td>
                    )}
                    <td className="px-6 py-4">
                      <div className="flex items-start space-x-3">
                        {doc.thumbnail_url ? (
                          <div className="relative w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden group">
                            <img
                              src={doc.thumbnail_url}
                              alt={doc.title}
                              loading="lazy"
                              className="w-full h-full object-cover transition-transform group-hover:scale-110"
                            />
                          </div>
                        ) : (
                          <div className="w-16 h-16 flex-shrink-0 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                            <FileText className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                          </div>
                        )}
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-semibold text-gray-900 dark:text-white truncate">{doc.title}</span>
                          {doc.tags && doc.tags.length > 0 && (
                            <div className="flex gap-1 flex-wrap mt-1">
                              {doc.tags.map((tag) => (
                                <span
                                  key={tag}
                                  className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300 text-xs rounded-full"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    {activeTab === 'shared' && (
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          doc.shared_role === 'editor'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900/50 dark:text-gray-300'
                        }`}>
                          {doc.shared_role}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200 font-medium">
                      {new Date(doc.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200 font-medium">
                      {new Date(doc.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link to={`/editor/${doc.id}`}>
                        <Button variant="ghost" size="sm">
                          {t('documents.open')}
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
            {t('documents.noDocumentsFound')}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 mb-4 text-sm">
            {searchQuery
              ? t('documents.noSearchMatch')
              : activeTab === 'owned'
              ? t('documents.createFirstDocument')
              : t('documents.noSharedDocuments')}
          </p>
          {activeTab === 'owned' && (
            <Link to="/editor">
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                {t('documents.create')}
              </Button>
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

export default Documents;
